import { useBoolean } from '@fluentui/react-hooks';
import { FontIcon, Panel, PanelType, Stack, Text } from '@fluentui/react';
import { useEffect, useRef, useState } from 'react';
import {
  IAction,
  IPermission,
  IUpdatePermissionsRequest,
  getActions,
  getPermissions,
  updatePermissions,
} from '../../services/permissionService';
import { useAtomValue, useSetAtom } from 'jotai';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import UserOrGroupSelector from '../../common/selectors/UsersOrGroupSelectors';
import {
  PagedDataGrid,
  ToggleSettingsList,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import {
  Button,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';

type ItemType = { id: number; name: string; isGroup: boolean };

type EditingTarget = {
  isGroup: boolean;
  userOrGroupId: number;
  name: string;
};

const PermissionsV2 = () => {
  const [
    isUsersSelectorOpen,
    { setTrue: showUsersSelector, setFalse: hideUsersSelector },
  ] = useBoolean(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userOrGroups, setUserOrGroups] = useState<ItemType[]>([]);
  const [permissions, setPermissions] = useState<IPermission[]>([]);

  // Drawer editing state: the user/group being edited plus a working set of
  // selected action ids, seeded from the saved permissions. Editing is scoped
  // to a single target so Save/Discard are unambiguous.
  const [editingTarget, setEditingTarget] = useState<EditingTarget>();
  const [draftActionIds, setDraftActionIds] = useState<Set<number>>(new Set());
  const [savedActionIds, setSavedActionIds] = useState<Set<number>>(new Set());

  const profileData = useAtomValue(profileDataAtom);
  const isAdmin = profileData.roles.includes(appRoles.Admin);

  const allActions = useRef<IAction[]>([]);

  const fetchData = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    setLoading(true);
    try {
      allActions.current = await getActions(abortController);
      const allPermissions = await getPermissions(abortController);

      if (!allActions.current || !allPermissions) return;

      allPermissions.forEach((p) => {
        p.groupId = p.groupId == null ? undefined : p.groupId;
        p.userId = p.userId == null ? undefined : p.userId;
      });

      const result: ItemType[] = [];
      allPermissions.forEach((perm) => {
        if (
          !result.find(
            (row) =>
              (row.isGroup && row.id === perm.groupId) ||
              (!row.isGroup && row.id === perm.userId)
          )
        ) {
          result.push({
            id: perm.groupId ?? perm.userId ?? 0,
            name: perm.name,
            isGroup: !!perm.groupId,
          });
        }
      });
      setPermissions(allPermissions);
      setUserOrGroups(result);
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
      setLoading(false);
    }
    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, [refreshCount]);

  const openEditor = (item: ItemType) => {
    const seeded = new Set(
      permissions
        .filter((p) => (item.isGroup ? p.groupId : p.userId) === item.id)
        .map((p) => p.actionId)
    );
    setEditingTarget({
      isGroup: item.isGroup,
      userOrGroupId: item.id,
      name: item.name,
    });
    setSavedActionIds(seeded);
    setDraftActionIds(new Set(seeded));
  };

  const closeEditor = () => {
    setEditingTarget(undefined);
    setDraftActionIds(new Set());
    setSavedActionIds(new Set());
  };

  const toggleAction = (actionId: number, checked: boolean) => {
    setDraftActionIds((current) => {
      const next = new Set(current);
      if (checked) next.add(actionId);
      else next.delete(actionId);
      return next;
    });
  };

  const hasUnsavedChanges =
    draftActionIds.size !== savedActionIds.size ||
    [...draftActionIds].some((id) => !savedActionIds.has(id));

  const handleSave = () => {
    if (!editingTarget) return;

    // Rebuild the full permissions payload: every other target's perms are kept
    // as-is, the edited target's actions are replaced by the draft selection.
    const others = permissions.filter(
      (p) =>
        (editingTarget.isGroup ? p.groupId : p.userId) !==
        editingTarget.userOrGroupId
    );
    const edited: IPermission[] = [...draftActionIds].map((actionId) => ({
      id: 0,
      name: '',
      actionId,
      userId: editingTarget.isGroup ? undefined : editingTarget.userOrGroupId,
      groupId: editingTarget.isGroup ? editingTarget.userOrGroupId : undefined,
    }));
    const nextPermissions = [...others, ...edited];

    const payload: IUpdatePermissionsRequest = { permissions: [] };
    nextPermissions.forEach((perm) => {
      const currPerm = payload.permissions.find(
        (p) => p.groupId === perm.groupId && p.userId === perm.userId
      );
      if (!currPerm) {
        payload.permissions.push({
          userId: perm.userId ? perm.userId : undefined,
          groupId: perm.groupId ? perm.groupId : undefined,
          actions: [perm.actionId],
        });
      } else {
        currPerm.actions.push(perm.actionId);
      }
    });

    const abortController = new AbortController();
    setIsSaving(true);
    setIsInProgress(true);
    updatePermissions(abortController, payload)
      .then((data) => {
        if (data.message === 'OK') {
          setSuccessMessage('Permission updated successfully.');
          setPermissions(nextPermissions);
          closeEditor();
        } else {
          setErrorMessage(data.message);
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        setErrorMessage(error.message);
      })
      .finally(() => {
        setIsSaving(false);
        setIsInProgress(false);
      });
  };

  const commandBarItems: ICommandBarItem[] = [
    {
      key: 'new',
      text: 'New',
      icon: <FontIcon iconName="Add" />,
      onClick: () => showUsersSelector(),
    },
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: () => {
        closeEditor();
        setRefreshCount(refreshCount + 1);
      },
    },
  ];

  const columns: DataGridColumn<ItemType>[] = [
    {
      key: 'name',
      name: 'User|Group Name',
      minWidth: 300,
      ellipsis: true,
    },
    {
      key: 'id',
      name: 'User|Group Id',
      width: 100,
      renderCell: (item) => (
        <Stack horizontal tokens={{ childrenGap: 5 }}>
          <Text variant="large">{item.id}</Text>
          <Text variant="small">{item.isGroup ? `(Group)` : `(User)`}</Text>
        </Stack>
      ),
    },
    {
      key: 'action',
      name: '',
      width: 50,
      renderCell: (item) => (
        <FontIcon
          iconName="Edit"
          style={{ cursor: 'pointer' }}
          onClick={() => openEditor(item)}
        />
      ),
    },
  ];

  return (
    <>
      <div style={{ width: 500 }}>
        <PagedDataGrid<ItemType>
          columns={columns}
          items={userOrGroups}
          loading={loading}
          commandBarItems={commandBarItems}
        />
      </div>

      <Panel
        isOpen={!!editingTarget}
        onDismiss={closeEditor}
        type={PanelType.medium}
        headerText={
          editingTarget ? `Permissions — ${editingTarget.name}` : 'Permissions'
        }
        closeButtonAriaLabel="Close"
        isFooterAtBottom
        onRenderFooterContent={() => (
          <FluentProvider theme={webLightTheme}>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Button
                appearance="primary"
                disabled={!isAdmin || !hasUnsavedChanges || isSaving}
                onClick={handleSave}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                appearance="secondary"
                disabled={!hasUnsavedChanges || isSaving}
                onClick={() => setDraftActionIds(new Set(savedActionIds))}
              >
                Discard
              </Button>
            </Stack>
          </FluentProvider>
        )}
      >
        <FluentProvider theme={webLightTheme}>
          <ToggleSettingsList<string>
            items={allActions.current.map((action) => ({
              key: String(action.id),
              label: action.name,
            }))}
            values={Object.fromEntries(
              allActions.current.map((action) => [
                String(action.id),
                draftActionIds.has(action.id),
              ])
            )}
            onChange={(key, checked) => toggleAction(Number(key), checked)}
            disabled={!isAdmin || isSaving}
          />
        </FluentProvider>
      </Panel>

      <UserOrGroupSelector
        isOpen={isUsersSelectorOpen}
        hideUsersAndGroupsSelector={hideUsersSelector}
        onUserOrGroupSelected={(
          id: number,
          isGroup: boolean,
          username?: string
        ) => {
          const newItem: ItemType = { id, name: username ?? '', isGroup };
          setUserOrGroups((current) =>
            current.find((u) => u.id === id && u.isGroup === isGroup)
              ? current
              : [...current, newItem]
          );
          openEditor(newItem);
        }}
      />
    </>
  );
};

export default PermissionsV2;
